import type { WorkerRole, WorkerTier } from '../../core/index.js'
import type { CalloutEvent, NoShowEvent, ShiftCompleteEvent } from '../../partner/index.js'
import cast from './trainingCast.json'
import type { DemoFacility } from './demoData.js'
import type { FacilityAddress } from '../../facilities/index.js'

/**
 * The training materials' cast (docs/scoring/scoring-engine-training-plan.md §3)
 * as the demo client seeds it: Harbor View Care Center, six workers whose
 * months are fixed scripts, and their facility manager. The file this
 * reads is generated by the API (`npm run cli -- training-examples
 * --write-web`), so the demo client shows the same people and the same
 * numbers as the manual.
 *
 * The story's "October" is replayed into last month and its "November"
 * into this month up to yesterday, so a demo set up on any date has a
 * closed month with the manual's finals and a running month underway.
 */

type Month = 'october' | 'november'

interface ScriptedShift {
  day: number
  kind: 'completed' | 'callout' | 'no_show'
  lateBy: number
  leftEarlyBy: number
  noticeHours: number | null
  clinicalBridge: boolean
}

interface TrainingSeedWorker {
  workerId: string
  name: string
  role: string
  archetype: string
  hireDate: string | null
  startingTier: string
  startingScore: number
  chartingPct: number | null
  months: Record<Month, ScriptedShift[]>
}

const SEED = cast as {
  facility: { facilityId: string; name: string; timezone: string; unitId: string; address: FacilityAddress }
  staff: { facilityManager: { firstName: string; lastName: string }; operator: { firstName: string; lastName: string } }
  workers: TrainingSeedWorker[]
}

export const TRAINING_FACILITY: DemoFacility = {
  facilityId: SEED.facility.facilityId,
  name: SEED.facility.name,
  timezone: SEED.facility.timezone,
  address: SEED.facility.address,
  administrator: SEED.staff.facilityManager,
}

export const TRAINING_OPERATOR = SEED.staff.operator

export const TRAINING_WORKER_IDS: readonly string[] = SEED.workers.map((worker) => worker.workerId)
/** The cast's roles, for counting how many more of each role Harbor View's roster needs (demoStaffing.ts). */
export const TRAINING_WORKER_ROLES: readonly WorkerRole[] = SEED.workers.map((worker) => worker.role as WorkerRole)

/** The cast member the Worker guide follows (docs/scoring/scoring-engine-training-plan.md, T8): the demo gives them a login. */
export const TRAINING_WORKER_LOGIN_ID = 'hv-devon-price'

/** A cast member's name and id, for the login the demo creates. */
export function trainingWorkerNamed(workerId: string): { workerId: string; facilityId: string; name: string } | null {
  const worker = SEED.workers.find((candidate) => candidate.workerId === workerId)
  return worker ? { workerId: worker.workerId, facilityId: TRAINING_FACILITY.facilityId, name: worker.name } : null
}

const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
const MINUTE_MS = 60 * 1000
/** 7 AM in Harbor View's zone during daylight time; an hour off in winter, which changes no count. */
const SHIFT_START_HOUR_UTC = 11
const SHIFT_HOURS = 8

/** The month the story's October lands in (last month) and its November (this month), as UTC month starts. */
export function trainingMonths(now: Date): Record<Month, number> {
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth()
  return { october: Date.UTC(year, month - 1, 1), november: Date.UTC(year, month, 1) }
}

/** Roster rows for the cast, in the columns `buildDemoWorkersCsvFiles` writes plus `hireDate`. */
/** The cast's roster rows; `callOutsByWorker` is each worker's count among this month's scripted outcomes, which is the reading the engine will set anyway. */
export function trainingWorkerRows(now: Date, bidWindowOffsetDays: Record<WorkerTier, number>, callOutsByWorker: ReadonlyMap<string, number>): (string | number)[][] {
  const scoreResetsAt = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  return SEED.workers.map((worker) => {
    const tier = worker.startingTier as WorkerTier
    return [
      worker.workerId,
      SEED.facility.facilityId,
      worker.name,
      worker.role as WorkerRole,
      tier,
      worker.startingScore,
      callOutsByWorker.get(worker.workerId) ?? '',
      worker.chartingPct ?? '',
      new Date(scoreResetsAt).toISOString(),
      new Date(scoreResetsAt + bidWindowOffsetDays[tier] * DAY_MS).toISOString(),
      worker.hireDate ?? '',
    ]
  })
}

export type TrainingOutcomeEvent =
  | { kind: 'shift-complete'; idempotencyKey: string; payload: ShiftCompleteEvent }
  | { kind: 'callout'; idempotencyKey: string; payload: CalloutEvent }
  | { kind: 'no-show'; idempotencyKey: string; payload: NoShowEvent }

function outcomeEvent(worker: TrainingSeedWorker, shift: ScriptedShift, monthStart: number): TrainingOutcomeEvent {
  const start = monthStart + (shift.day - 1) * DAY_MS + SHIFT_START_HOUR_UTC * HOUR_MS
  const end = start + SHIFT_HOURS * HOUR_MS
  const iso = (ms: number): string => new Date(ms).toISOString()
  const base = {
    facility_id: SEED.facility.facilityId,
    worker_id: worker.workerId,
    shift_date: iso(start).slice(0, 10),
    scheduled_start: iso(start),
    scheduled_end: iso(end),
    unit_id: SEED.facility.unitId,
  }
  const idempotencyKey = `cast-${worker.workerId}-${base.shift_date}`
  switch (shift.kind) {
    case 'no_show':
      return { kind: 'no-show', idempotencyKey, payload: base }
    case 'callout':
      return { kind: 'callout', idempotencyKey, payload: { ...base, reported_at: iso(start - (shift.noticeHours ?? 0) * HOUR_MS) } }
    case 'completed':
      return {
        kind: 'shift-complete',
        idempotencyKey,
        payload: { ...base, actual_start: iso(start + shift.lateBy * MINUTE_MS), actual_end: iso(end - shift.leftEarlyBy * MINUTE_MS), ...(shift.clinicalBridge ? { clinical_bridge: true } : {}) },
      }
  }
}

/** Every cast shift of last month, and this month's up to yesterday. */
export function buildTrainingOutcomeEvents(now: Date): TrainingOutcomeEvent[] {
  const months = trainingMonths(now)
  const today = now.getUTCDate()
  return SEED.workers.flatMap((worker) => [
    ...worker.months.october.map((shift) => outcomeEvent(worker, shift, months.october)),
    ...worker.months.november.filter((shift) => shift.day < today).map((shift) => outcomeEvent(worker, shift, months.november)),
  ])
}
